/**
 * A workbench's screen over the relay (a `mirror.subscribe` channel, packages/shared/src/schemas/mirror.ts): the path
 * on Mojo Up AI Cloud, and on an Enterprise machine off the mesh. One socket per screen, opened with a single-use
 * ticket so no token sits in a URL; frames come in parts, are put back together, and their credit is granted back
 * once each is drawn, so a slow browser slows the machine rather than filling a queue.
 */

import {
  MIRROR_CHANNEL_METHOD,
  MirrorFrameAssembler,
  RELAY_CHANNEL_INITIAL_CREDIT,
  RELAY_CHANNEL_SUBPROTOCOL,
  mirrorOpenedSchema,
  relayToClientFrameSchema,
  type ChannelCloseCode,
  type MirrorInputEvent,
  type MirrorFrameReady,
  type MirrorOpened,
} from '@aioe/shared';

import { routes } from '../api/client.ts';

export interface MirrorHandlers {
  onOpened(opened: MirrorOpened | undefined, scopes: string[]): void;
  /** A whole frame; call `drawn` once it is on screen so the machine may send the next. */
  onFrame(frame: MirrorFrameReady, drawn: () => void): void;
  onClosed(close: { code: ChannelCloseCode; reason?: string }): void;
}

export interface MirrorChannel {
  /** One event in the drawn frame's coordinates: the same language as the mesh's `POST …/mirror/input`. */
  input(event: MirrorInputEvent): void;
  close(): void;
}

const PING_MS = 30_000;
/** The relay's count of a frame is its device-side JSON; this one is the relay's copy, so grant a little over. */
const SLACK = 64;
const utf8 = new TextEncoder();

/**
 * Opens the screen. `control` asks to click and type: the ticket then needs a recent sign-in, and a stale one
 * rejects with the API's `sign_in_again` (ApiError) before anything opens.
 */
export async function openMirror(deviceId: string, opts: { control: boolean; maxWidth: number }, h: MirrorHandlers): Promise<MirrorChannel> {
  const ticket = await routes.channelTicket(deviceId, opts.control ? { stepUp: true } : {});
  const ws = new WebSocket(ticket.url, RELAY_CHANNEL_SUBPROTOCOL);
  const frames = new MirrorFrameAssembler();
  let ch: string | undefined;
  let ended = false;
  const send = (f: unknown) => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify(f));
  const grant = (bytes: number) => bytes > 0 && ch && send({ t: 'credit', ch, bytes });
  const end = (code: ChannelCloseCode, reason?: string) => {
    if (ended) return;
    ended = true;
    clearInterval(ping);
    h.onClosed({ code, ...(reason ? { reason } : {}) });
  };
  const ping = setInterval(() => send({ t: 'ping', at: Date.now() }), PING_MS);
  ws.onopen = () => send({ t: 'open', ref: 'screen', rpc: { jsonrpc: '2.0', id: 'screen', method: MIRROR_CHANNEL_METHOD, params: { maxWidth: opts.maxWidth } }, input: opts.control });
  ws.onmessage = (ev) => {
    const text = typeof ev.data === 'string' ? ev.data : '';
    let parsed: ReturnType<typeof relayToClientFrameSchema.safeParse>;
    try {
      parsed = relayToClientFrameSchema.safeParse(JSON.parse(text));
    } catch {
      return;
    }
    if (!parsed.success) return;
    const f = parsed.data;
    if (f.t === 'opened') {
      ch = f.ch;
      const o = mirrorOpenedSchema.safeParse(f.result);
      h.onOpened(o.success ? o.data : undefined, f.scopes);
    } else if (f.t === 'data' && f.ch === ch) {
      const ready = frames.push(f.data, utf8.encode(text).length + SLACK);
      if (ready) h.onFrame(ready, () => grant(ready.bytes));
      // A frame larger than half the credit would wait forever for credit granted only once it is drawn.
      else if (frames.pending() >= RELAY_CHANNEL_INITIAL_CREDIT / 2) grant(frames.drain());
    } else if (f.t === 'closed') {
      end(f.code, f.reason);
      ws.close(1000);
    } else if (f.t === 'error') {
      end(f.code, f.reason);
    }
  };
  ws.onclose = () => end('workbench_offline');
  return {
    input: (event) => ch && send({ t: 'input', ch, data: event }),
    close: () => {
      ended = true;
      clearInterval(ping);
      if (ch) send({ t: 'close', ch });
      ws.close(1000);
    },
  };
}
